import { ORDERS_URL, PAYPAL_URL, UPLOAD_URL } from '../constants';
import { pickValidDate } from '../utils/formatDate';
import { apiSlice, ORDER_LIST_TAG } from './apiSlice';

const ORDER_STATUS_KEYS = [
	'paymentStatus',
	'deliveryStatus',
	'approvalStatus',
	'isApproved',
	'isDelivered',
	'isAccepted',
	'isCompleted',
	'isFinalCostConfirmed',
	'isDeliveryPaid',
	'isAcceptPaid',
	'isPlaceOrderPaid',
	'currentAccountsReceivable',
	'approvedAt',
	'approvedResult',
	'deliveredAt',
	'deliveredResult',
	'acceptedAt',
	'acceptedResult',
	'completedAt',
	'completedResult',
	'finalCost',
	'finalCostConfirmedAt',
	'finalCostConfirmedResult',
	'rewardSummary',
	'rewardSummaryReady',
	'deliveryPaidAt',
	'deliveryPaidResult',
	'acceptPaidAt',
	'acceptPaidResult',
	'placeOrderPaidAt',
	'placeOrderPaidResult',
];

const serializeOrderListQueryParams = ({ globalFilter, sorting, columnFilters, pageIndex, pageSize }) => ({
	globalFilter: typeof globalFilter === 'string' && globalFilter.trim() ? globalFilter.trim() : undefined,
	pageIndex,
	pageSize,
	sorting: Array.isArray(sorting) && sorting.length > 0 ? JSON.stringify(sorting) : undefined,
	columnFilters: Array.isArray(columnFilters) && columnFilters.length > 0 ? JSON.stringify(columnFilters) : undefined,
});

const isValidDateInput = (value) => Boolean(pickValidDate(value));

const resolveScheduleNodeCompletedAt = (incoming, existing) =>
	pickValidDate(incoming?.completedAt, incoming?.completedResult?.update_time, existing?.completedAt, existing?.completedResult?.update_time);

const mergeScheduleNodesIntoDraft = (draft, incoming) => {
	if (!Array.isArray(incoming)) return;
	if (!Array.isArray(draft.scheduleNodes) || draft.scheduleNodes.length === 0) {
		draft.scheduleNodes = incoming;
		return;
	}
	const incomingById = new Map(incoming.map((n) => [String(n._id ?? n.id), n]));
	draft.scheduleNodes = draft.scheduleNodes.map((existing) => {
		const next = incomingById.get(String(existing._id ?? existing.id));
		if (!next) return existing;
		const merged = { ...existing, ...next };
		if ('isCompleted' in next) merged.isCompleted = next.isCompleted;
		const completedAt = resolveScheduleNodeCompletedAt(next, existing);
		if (completedAt) merged.completedAt = completedAt;
		return merged;
	});
};

const copyStatusFields = (draft, data) => {
	for (const key of ORDER_STATUS_KEYS) {
		if (!(key in data)) continue;
		if (key.endsWith('At') && !isValidDateInput(data[key]) && isValidDateInput(draft[key])) continue;
		draft[key] = data[key];
	}
};

const mergeUiView = (draft, view) => {
	if (!view) return;
	draft.uiHints = { ...(draft.uiHints ?? {}), ...view };
};

const applyOrderDetailsFields = (draft, data, fields) => {
	if (!draft || !data || !fields?.length) return;
	const fieldSet = new Set(fields);

	if (fieldSet.has('contracts') && data.contracts != null) draft.contracts = data.contracts;
	if (fieldSet.has('invoices') && data.invoices != null) draft.invoices = data.invoices;
	if (fieldSet.has('orderNotes') && data.orderNotes != null) draft.orderNotes = data.orderNotes;

	if (fieldSet.has('workflow')) {
		const incomingUpdated = Date.parse(data.updatedAt) || 0;
		const draftUpdated = Date.parse(draft.updatedAt) || 0;
		// 发货后立刻验收时，在途的旧 GET 可能晚到并盖掉更新的验收 patch
		const isStaleWorkflowSnapshot = incomingUpdated > 0 && draftUpdated > 0 && incomingUpdated < draftUpdated;
		if (!isStaleWorkflowSnapshot) {
			if (data.scheduleNodes != null) mergeScheduleNodesIntoDraft(draft, data.scheduleNodes);
			if (data.accountsReceivables != null) {
				draft.accountsReceivables = data.accountsReceivables.map((ar) => {
					if (!ar || typeof ar !== 'object') return ar;
					const next = { ...ar };
					if (next.scheduleNode != null) next.scheduleNode = String(next.scheduleNode);
					if ('isPaid' in next) {
						const paid = next.isPaid;
						next.isPaid = typeof paid === 'boolean' ? paid : paid === 'true' || paid === 'True' || paid === 'TRUE';
					}
					return next;
				});
			}
			copyStatusFields(draft, data);
			if (data.updatedAt != null) draft.updatedAt = data.updatedAt;
			mergeUiView(draft, data.workflowView);
		}
	}

	if (fieldSet.has('brandCorrection')) {
		if (data.brandCorrectionAmounts != null) draft.brandCorrectionAmounts = data.brandCorrectionAmounts;
		mergeUiView(draft, data.correctionView);
	}

	if (fieldSet.has('orderItemsCorrection') && Array.isArray(data.orderItems) && Array.isArray(draft.orderItems)) {
		const byId = new Map(data.orderItems.map((item) => [String(item._id), item]));
		for (const item of draft.orderItems) {
			const incoming = byId.get(String(item._id));
			if (incoming?.correctionAmount !== undefined) item.correctionAmount = incoming.correctionAmount;
		}
	}

	if (fieldSet.has('performance')) {
		if (data.performanceRulesSnapshot != null) draft.performanceRulesSnapshot = data.performanceRulesSnapshot;
		if ('performanceRulesDivergedFromGlobal' in data) draft.performanceRulesDivergedFromGlobal = data.performanceRulesDivergedFromGlobal;
		if ('rewardSummary' in data) draft.rewardSummary = data.rewardSummary;
		if ('rewardSummaryReady' in data) draft.rewardSummaryReady = data.rewardSummaryReady;
		if (data.uiHints) draft.uiHints = data.uiHints;
	}

	if (data.viewerCapabilities) draft.viewerCapabilities = data.viewerCapabilities;
};

const normalizeOrderDetailsCacheId = (orderId) => {
	if (orderId == null || orderId === '') return '';
	return String(orderId);
};

/** 最近一次流程 mutation 写入的快照，用于挡住随后到达的旧 GET */
const lastWorkflowMutationSnapshotByOrderId = new Map();

const orderDetailsPatchOnFulfilled =
	(resolveOrderId, fields) =>
	async (arg, { dispatch, queryFulfilled }) => {
		try {
			const { data } = await queryFulfilled;
			const orderId = normalizeOrderDetailsCacheId(resolveOrderId(arg));
			if (!orderId || !data) return;
			patchOrderDetailsCache(dispatch, orderId, data, fields);
			// 只失效列表/待办，不失效当前详情：否则 GET 会盖掉刚 patch 的发货/验收，弹窗内容被卸掉再挂上
			if (Array.isArray(fields) && fields.includes('workflow')) {
				dispatch(ordersApiSlice.util.invalidateTags([ORDER_LIST_TAG]));
			}
		} catch {
			/* 保留原缓存 */
		}
	};

/** 发货/验收等 mutation 成功后写详情缓存（供组件在 unwrap 后补写，避免并发 refetch 盖掉） */
export const patchOrderDetailsCache = (dispatch, orderId, data, fields = ['workflow']) => {
	const normalizedId = normalizeOrderDetailsCacheId(orderId);
	if (!normalizedId || !data) return;
	if (Array.isArray(fields) && fields.includes('workflow')) {
		const incomingUpdatedAt = Date.parse(data.updatedAt) || Date.now();
		const prev = lastWorkflowMutationSnapshotByOrderId.get(normalizedId);
		const prevUpdatedAt = Date.parse(prev?.data?.updatedAt) || 0;
		if (!prev || incomingUpdatedAt >= prevUpdatedAt) {
			lastWorkflowMutationSnapshotByOrderId.set(normalizedId, { data, fields, savedAt: Date.now() });
		}
	}
	dispatch(
		ordersApiSlice.util.updateQueryData('getOrderDetails', normalizedId, (draft) => {
			applyOrderDetailsFields(draft, data, fields);
		}),
	);
};

export const ordersApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		createOrder: builder.mutation({
			query: (order) => ({
				url: ORDERS_URL,
				method: 'POST',
				body: { ...order },
			}),
			// 驳回重提复用同一 _id：必须失效详情缓存，否则待办打开仍是驳回态
			invalidatesTags: (result, error, arg) => {
				if (error) return [];
				const id = result?._id ?? arg?.resubmitOrderId;
				const tags = [ORDER_LIST_TAG, 'Approval'];
				if (id) tags.push({ type: 'Order', id: String(id) });
				return tags;
			},
		}),
		validateOrderPlacement: builder.mutation({
			query: (body) => ({
				url: `${ORDERS_URL}/validate-placement`,
				method: 'POST',
				body,
			}),
		}),
		uploadOrderAttachment: builder.mutation({
			query: (formData) => ({
				url: `${UPLOAD_URL}/order/attachment`,
				method: 'POST',
				body: formData,
				// RTK Query 会自动检测 FormData 并设置正确的 headers
			}),
		}),
		getOrderDetails: builder.query({
			query: (orderId) => ({
				url: `${ORDERS_URL}/${orderId}`,
			}),
			providesTags: (result, error, orderId) => (result ? [{ type: 'Order', id: orderId }] : []),
			keepUnusedDataFor: 60,
			refetchOnMountOrArgChange: true,
			async onQueryStarted(orderId, { queryFulfilled, dispatch }) {
				const normalizedId = normalizeOrderDetailsCacheId(orderId);
				try {
					const { data } = await queryFulfilled;
					// 驳回为终态口径：清掉流程快照，禁止旧 workflow patch 盖回「申请中」
					if (data?.approvalStatus === 'reject') {
						lastWorkflowMutationSnapshotByOrderId.delete(normalizedId);
						return;
					}
					const snap = lastWorkflowMutationSnapshotByOrderId.get(normalizedId);
					if (!snap?.data) return;
					// 快照超过 15s 不再强行盖回，避免挡住用户手动刷新后的真实数据
					if (Date.now() - (snap.savedAt || 0) > 15000) {
						lastWorkflowMutationSnapshotByOrderId.delete(normalizedId);
						return;
					}
					const snapUpdatedAt = Date.parse(snap.data.updatedAt) || 0;
					const incomingUpdatedAt = Date.parse(data?.updatedAt) || 0;
					if (snapUpdatedAt > 0 && incomingUpdatedAt > 0 && incomingUpdatedAt < snapUpdatedAt) {
						patchOrderDetailsCache(dispatch, normalizedId, snap.data, snap.fields);
					} else if (incomingUpdatedAt >= snapUpdatedAt) {
						lastWorkflowMutationSnapshotByOrderId.delete(normalizedId);
					}
				} catch {
					/* 保留原缓存 */
				}
			},
		}),
		payOrders: builder.mutation({
			query: ({ orderOId, details }) => ({
				url: `${ORDERS_URL}/${orderOId}/pay`,
				method: 'PUT',
				body: { ...details },
			}),
			onQueryStarted: orderDetailsPatchOnFulfilled((arg) => arg.orderOId, ['workflow']),
		}),
		getPayPalClientId: builder.query({
			query: () => ({
				url: PAYPAL_URL,
			}),
			keepUnusedDataFor: 5,
		}),
		getMyOrders: builder.query({
			query: () => ({
				url: `${ORDERS_URL}/mine`,
			}),
			providesTags: [ORDER_LIST_TAG],
			keepUnusedDataFor: 5,
		}),
		getMyRejectedOrders: builder.query({
			query: () => ({
				url: `${ORDERS_URL}/mine/rejected`,
			}),
			providesTags: [ORDER_LIST_TAG],
			keepUnusedDataFor: 5,
		}),
		deleteMyRejectedOrders: builder.mutation({
			query: (data) => ({
				url: `${ORDERS_URL}/mine/rejected`,
				method: 'DELETE',
				body: data,
			}),
			invalidatesTags: [ORDER_LIST_TAG, 'Order'],
		}),
		cancelMyRejectedOrders: builder.mutation({
			query: (data) => ({
				url: `${ORDERS_URL}/mine/rejected/cancel`,
				method: 'PUT',
				body: data,
			}),
			invalidatesTags: [ORDER_LIST_TAG, 'Order'],
		}),
		withdrawApplyingOrder: builder.mutation({
			query: (orderId) => ({
				url: `${ORDERS_URL}/${orderId}/withdraw`,
				method: 'PUT',
			}),
			invalidatesTags: [ORDER_LIST_TAG, 'Order', 'Approval', 'OrderDraft', 'Notification'],
		}),
		getManagerOrders: builder.query({
			query: (queryArgs) => ({
				url: `${ORDERS_URL}/mine/manager`,
				params: serializeOrderListQueryParams(queryArgs),
			}),
			providesTags: [ORDER_LIST_TAG],
			keepUnusedDataFor: 30,
		}),
		getFinanceOrders: builder.query({
			query: (queryArgs) => ({
				url: `${ORDERS_URL}/mine/finance`,
				params: serializeOrderListQueryParams(queryArgs),
			}),
			providesTags: [ORDER_LIST_TAG],
			keepUnusedDataFor: 30,
		}),
		getAssistantOrders: builder.query({
			query: (queryArgs) => ({
				url: `${ORDERS_URL}/mine/assistant`,
				params: serializeOrderListQueryParams(queryArgs),
			}),
			providesTags: [ORDER_LIST_TAG],
			keepUnusedDataFor: 30,
		}),
		getBuyerOrders: builder.query({
			query: (queryArgs) => ({
				url: `${ORDERS_URL}/mine/buyer`,
				params: serializeOrderListQueryParams(queryArgs),
			}),
			providesTags: [ORDER_LIST_TAG],
			keepUnusedDataFor: 30,
		}),
		getOrderListFilterOptions: builder.query({
			query: () => ({
				url: `${ORDERS_URL}/list-filter-options`,
			}),
			providesTags: [ORDER_LIST_TAG],
			keepUnusedDataFor: 60,
		}),
		getSaleOrders: builder.query({
			query: (queryArgs) => ({
				url: `${ORDERS_URL}/mine/sale`,
				params: serializeOrderListQueryParams(queryArgs),
			}),
			providesTags: [ORDER_LIST_TAG],
			keepUnusedDataFor: 30,
		}),
		getNewIndexOfToday: builder.query({
			query: () => ({
				url: `${ORDERS_URL}/index`,
			}),
			keepUnusedDataFor: 5,
		}),
		getNewIndexOfTodayBySelectedSale: builder.mutation({
			query: (sale) => ({
				url: `${ORDERS_URL}/index/sale/${sale._id}`,
			}),
			keepUnusedDataFor: 5,
		}),
		getOrders: builder.query({
			query: () => ({
				url: ORDERS_URL,
			}),
			keepUnusedDataFor: 5,
		}),
		addAccountsReceivable: builder.mutation({
			query: ({ orderId, details }) => ({
				url: `${ORDERS_URL}/${orderId}/ar/add`,
				method: 'PUT',
				body: { ...details },
			}),
			onQueryStarted: orderDetailsPatchOnFulfilled((arg) => arg.orderId, ['workflow']),
		}),
		addOrderNote: builder.mutation({
			query: ({ _id, data, type }) => ({
				url: `${ORDERS_URL}/${_id}/note/add/${type}`,
				method: 'POST',
				body: data,
			}),
			onQueryStarted: orderDetailsPatchOnFulfilled((arg) => arg._id, ['orderNotes']),
		}),
		addOrderContracts: builder.mutation({
			query: ({ _id, contractNumbers }) => ({
				url: `${ORDERS_URL}/${_id}/contracts/add`,
				method: 'PUT',
				body: { contractNumbers },
			}),
			invalidatesTags: ['OverdueStat'],
			onQueryStarted: orderDetailsPatchOnFulfilled((arg) => arg._id, ['contracts']),
		}),
		removeOrderContract: builder.mutation({
			query: ({ _id, contractNumber }) => ({
				url: `${ORDERS_URL}/${_id}/contracts/remove`,
				method: 'PUT',
				body: { contractNumber },
			}),
			invalidatesTags: ['OverdueStat'],
			onQueryStarted: orderDetailsPatchOnFulfilled((arg) => arg._id, ['contracts']),
		}),
		addOrderInvoices: builder.mutation({
			query: ({ _id, invoiceNumbers }) => ({
				url: `${ORDERS_URL}/${_id}/invoices/add`,
				method: 'PUT',
				body: { invoiceNumbers },
			}),
			invalidatesTags: ['OverdueStat'],
			onQueryStarted: orderDetailsPatchOnFulfilled((arg) => arg._id, ['invoices']),
		}),
		removeOrderInvoice: builder.mutation({
			query: ({ _id, invoiceNumber }) => ({
				url: `${ORDERS_URL}/${_id}/invoices/remove`,
				method: 'PUT',
				body: { invoiceNumber },
			}),
			invalidatesTags: ['OverdueStat'],
			onQueryStarted: orderDetailsPatchOnFulfilled((arg) => arg._id, ['invoices']),
		}),
		completeScheduleNode: builder.mutation({
			query: ({ orderId, scheduleNode }) => ({
				url: `${ORDERS_URL}/${orderId}/node/${scheduleNode}/complete`,
				method: 'PUT',
			}),
			onQueryStarted: orderDetailsPatchOnFulfilled((arg) => arg.orderId, ['workflow']),
		}),
		deliverOrder: builder.mutation({
			query: (arg) => {
				const orderId = typeof arg === 'string' || arg == null ? arg : arg.orderId;
				const body =
					typeof arg === 'string' || arg == null
						? {}
						: {
								...(arg.scheduleNode ? { scheduleNode: arg.scheduleNode } : {}),
								...(arg.accountsReceivableDetails ? { accountsReceivableDetails: arg.accountsReceivableDetails } : {}),
							};
				return {
					url: `${ORDERS_URL}/${orderId}/deliver`,
					method: 'PUT',
					body,
				};
			},
			onQueryStarted: orderDetailsPatchOnFulfilled((arg) => (typeof arg === 'string' || arg == null ? arg : arg.orderId), ['workflow']),
		}),
		deliverPayOrders: builder.mutation({
			query: ({ orderId, details }) => ({
				url: `${ORDERS_URL}/${orderId}/deliverpay`,
				method: 'PUT',
				body: { ...details },
			}),
			onQueryStarted: orderDetailsPatchOnFulfilled((arg) => arg.orderId, ['workflow']),
		}),
		acceptOrder: builder.mutation({
			query: (arg) => {
				const orderId = typeof arg === 'string' || arg == null ? arg : arg.orderId;
				const body =
					typeof arg === 'string' || arg == null
						? {}
						: {
								...(arg.scheduleNode ? { scheduleNode: arg.scheduleNode } : {}),
								...(arg.accountsReceivableDetails ? { accountsReceivableDetails: arg.accountsReceivableDetails } : {}),
							};
				return {
					url: `${ORDERS_URL}/${orderId}/accept`,
					method: 'PUT',
					body,
				};
			},
			onQueryStarted: orderDetailsPatchOnFulfilled((arg) => (typeof arg === 'string' || arg == null ? arg : arg.orderId), ['workflow']),
		}),
		approveOrder: builder.mutation({
			query: (data) => ({
				url: `${ORDERS_URL}/${data.orderId}/approvalResult/${data.approvalResult}`,
				method: 'PUT',
				body: data,
			}),
			invalidatesTags: (result, error, arg) => [{ type: 'Order', id: String(arg.orderId) }, 'Approval'],
			onQueryStarted: orderDetailsPatchOnFulfilled((arg) => arg.orderId, ['workflow']),
		}),
		checkOrderId: builder.mutation({
			query: (orderId) => ({
				url: `${ORDERS_URL}/${orderId}/check`,
			}),
			keepUnusedDataFor: 5,
		}),
		deleteOneOrder: builder.mutation({
			query: (orderId) => ({
				url: `${ORDERS_URL}/${orderId}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['Order'],
		}),
		deleteOrders: builder.mutation({
			query: (data) => ({
				url: `${ORDERS_URL}`,
				method: 'DELETE',
				body: data,
			}),
			invalidatesTags: ['Order'],
		}),
		adjustFinalCost: builder.mutation({
			query: ({ orderId, adjustedCost, reason, resubmittedFrom }) => ({
				url: `${ORDERS_URL}/${orderId}/adjust-final-cost`,
				method: 'PUT',
				body: { adjustedCost, reason, resubmittedFrom },
			}),
			invalidatesTags: ['Order', 'Approval'],
		}),
		applyApprovedFinalCostAdjustment: builder.mutation({
			query: ({ orderId, approvalId }) => ({
				url: `${ORDERS_URL}/${orderId}/apply-final-cost-adjustment`,
				method: 'PUT',
				body: { approvalId },
			}),
			invalidatesTags: ['Order'],
		}),
		confirmFinalCost: builder.mutation({
			query: ({ orderId }) => ({
				url: `${ORDERS_URL}/${orderId}/confirm-final-cost`,
				method: 'PUT',
				body: {},
			}),
			onQueryStarted: orderDetailsPatchOnFulfilled((arg) => arg.orderId, ['workflow']),
		}),
		updateBrandCorrectionAmount: builder.mutation({
			query: ({ orderId, brand, category, correctionAmount, corrections }) => ({
				url: `${ORDERS_URL}/${orderId}/brand-correction-amount`,
				method: 'PUT',
				body: Array.isArray(corrections) ? { corrections } : { brand, category, correctionAmount },
			}),
			onQueryStarted: orderDetailsPatchOnFulfilled((arg) => arg.orderId, ['brandCorrection']),
		}),
		updateOrderItemCorrectionAmount: builder.mutation({
			query: ({ orderId, orderItemId, correctionAmount }) => ({
				url: `${ORDERS_URL}/${orderId}/order-items/${orderItemId}/correction-amount`,
				method: 'PUT',
				body: { correctionAmount },
			}),
			onQueryStarted: orderDetailsPatchOnFulfilled((arg) => arg.orderId, ['orderItemsCorrection']),
		}),
		// 订单绩效规则
		reMatchOrderPerformanceRules: builder.mutation({
			query: ({ orderId }) => ({
				url: `${ORDERS_URL}/${orderId}/performance-rules/rematch`,
				method: 'POST',
				body: {},
			}),
			onQueryStarted: orderDetailsPatchOnFulfilled((arg) => arg.orderId, ['performance']),
		}),
		previewBulkReMatchOrderPerformanceRules: builder.mutation({
			query: ({ brand, startDate, endDate, onlyIncomplete }) => ({
				url: `${ORDERS_URL}/bulk-rematch-performance-rules/preview`,
				method: 'POST',
				body: { brand, startDate, endDate, onlyIncomplete },
			}),
		}),
		lookupOrderForBulkRematch: builder.mutation({
			query: ({ orderId, brand }) => ({
				url: `${ORDERS_URL}/bulk-rematch-performance-rules/lookup-order`,
				method: 'POST',
				body: { orderId, brand },
			}),
		}),
		lookupOrdersForBulkRematch: builder.mutation({
			query: ({ brand, orderMongoIds }) => ({
				url: `${ORDERS_URL}/bulk-rematch-performance-rules/lookup-orders`,
				method: 'POST',
				body: { brand, orderMongoIds },
			}),
		}),
		bulkReMatchOrderPerformanceRules: builder.mutation({
			query: ({ brand, startDate, endDate, onlyIncomplete, orderIds }) => ({
				url: `${ORDERS_URL}/bulk-rematch-performance-rules`,
				method: 'POST',
				body: { brand, startDate, endDate, onlyIncomplete, orderIds },
			}),
			invalidatesTags: ['Order'],
		}),
		updateOrderPerformanceRule: builder.mutation({
			query: ({ orderId, ruleId, performanceValue, profitMarginRanges }) => ({
				url: `${ORDERS_URL}/${orderId}/performance-rules/${ruleId}`,
				method: 'PUT',
				body: { performanceValue, profitMarginRanges },
			}),
			onQueryStarted: orderDetailsPatchOnFulfilled((arg) => arg.orderId, ['performance']),
		}),
		deleteOrderPerformanceRule: builder.mutation({
			query: ({ orderId, ruleId }) => ({
				url: `${ORDERS_URL}/${orderId}/performance-rules/${ruleId}`,
				method: 'DELETE',
			}),
			onQueryStarted: orderDetailsPatchOnFulfilled((arg) => arg.orderId, ['performance']),
		}),
	}),
});

export const {
	useCreateOrderMutation,
	useValidateOrderPlacementMutation,
	useUploadOrderAttachmentMutation,
	useGetOrderDetailsQuery,
	usePayOrdersMutation,
	useGetPayPalClientIdQuery,
	useGetMyOrdersQuery,
	useGetMyRejectedOrdersQuery,
	useDeleteMyRejectedOrdersMutation,
	useCancelMyRejectedOrdersMutation,
	useWithdrawApplyingOrderMutation,
	useGetManagerOrdersQuery,
	useGetAssistantOrdersQuery,
	useGetSaleOrdersQuery,
	useGetFinanceOrdersQuery,
	useGetBuyerOrdersQuery,
	useGetOrderListFilterOptionsQuery,
	useGetOrdersQuery,
	useCompleteScheduleNodeMutation,
	useDeliverOrderMutation,
	useDeliverPayOrdersMutation,
	useAcceptOrderMutation,
	useAddAccountsReceivableMutation,
	useApproveOrderMutation,
	useCheckOrderIdMutation,
	useGetNewIndexOfTodayQuery,
	useGetNewIndexOfTodayBySelectedSaleMutation,
	useDeleteOneOrderMutation,
	useDeleteOrdersMutation,
	useAddOrderNoteMutation,
	useAddOrderContractsMutation,
	useRemoveOrderContractMutation,
	useAddOrderInvoicesMutation,
	useRemoveOrderInvoiceMutation,
	useAdjustFinalCostMutation,
	useApplyApprovedFinalCostAdjustmentMutation,
	useConfirmFinalCostMutation,
	useUpdateBrandCorrectionAmountMutation,
	useUpdateOrderItemCorrectionAmountMutation,
	useReMatchOrderPerformanceRulesMutation,
	usePreviewBulkReMatchOrderPerformanceRulesMutation,
	useLookupOrderForBulkRematchMutation,
	useLookupOrdersForBulkRematchMutation,
	useBulkReMatchOrderPerformanceRulesMutation,
	useUpdateOrderPerformanceRuleMutation,
	useDeleteOrderPerformanceRuleMutation,
} = ordersApiSlice;
